<template>
    <div class="mb-3">
        <GoogleMaps
            :title="null"
            :addressLabel="addressLabel"
            v-model:fullAddress="addressObject.fullAddress"
            v-model:street="addressObject.street"
            v-model:streetNumber="addressObject.streetNumber"
            v-model:unitNumber="addressObject.unitNumber"
            v-model:postalCode="addressObject.postalCode"
            v-model:city="addressObject.city"
            v-model:province="addressObject.province"
            :fieldValidator="fieldValidator"
            :isStepReadOnly="isStepReadOnly"
            :hasPrepopAddress="hasPrepopAddress"
            :country="config.country"
            :lang="config.lang"
        />
    </div>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue';
import GoogleMaps from '@/components/form/services/GoogleMaps.vue';
import { storageGetJson, storageRemove, storageSet } from '@/utils/safe-storage.js';

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    addressLabel: {
        type: String,
        required: false,
        default: '',
    },
    value: {
        required: true,
    },
    fieldValidator: {
        type: Object,
        default: () => ({}),
    },
    isStepReadOnly: {
        type: Boolean,
        required: false,
        default: false,
    },
    prepopAddress: {
        type: String,
        required: false,
        default: false,
    },
    addressProp: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const emit = defineEmits(["update:model-value", "input", "validateAddress"]);

const { app, config, updateApplicationField } = inject('app');

const addressObject = ref({
    fullAddress: "",
    street: "",
    streetNumber: "",
    unitNumber: "",
    postalCode: "",
    city: "",
    province: "",
});

const hasPrepopAddress = ref(false);
const localStorageAddressKey = computed(() => `comparehub-user-address-${config.value.country?.toLowerCase() ?? 'ca'}`);
const localStorageAddressExpiryHours = 24;

function extractCivicNumber(addressString) {
    const pattern = /^\d+(-\d+)?/;
    const match = addressString.match(pattern);
    return match ? match[0] : null;
}

function getAddressFromLocalStorage() {
    const now = new Date();
    let foundAddressInLocalStorage = false;

    const storedAddressData = storageGetJson(localStorageAddressKey.value);
    if (typeof storedAddressData?.addressData === 'object' && storedAddressData.addressData !== null && new Date(storedAddressData.expiry) > now) {
        for (const field of Object.keys(addressObject.value)) {
            if (field in storedAddressData.addressData) {
                foundAddressInLocalStorage = true;
                addressObject.value[field] = storedAddressData.addressData[field];
            }
        }
    }
    return foundAddressInLocalStorage;
}

function storeAddressInLocalStorage() {
    const now = new Date();
    const expirationTime = new Date(now.getTime() + (60 * 60 * 1000 * localStorageAddressExpiryHours));

    storageRemove(localStorageAddressKey.value);

    const storeNewAddressData = {
        addressData: addressObject.value,
        expiry: expirationTime,
    };
    storageSet(localStorageAddressKey.value, JSON.stringify(storeNewAddressData));
}

function validateAddress() {
    emit("validateAddress", addressObject.value);
    if (props.fieldValidator?.isValid) {
        storeAddressInLocalStorage();
    }
}

watch(addressObject, (val) => {
    updateApplicationField(props.name, val);
    validateAddress();
}, { deep: true });

if (getAddressFromLocalStorage()) {
    hasPrepopAddress.value = true;
} else {
    let civicNumber = null;
    let addressStreet = null;

    if (props.prepopAddress) {
        hasPrepopAddress.value = true;
        civicNumber = extractCivicNumber(props.prepopAddress);
        addressStreet = civicNumber ? props.prepopAddress.replace(civicNumber, '').trim() : props.prepopAddress;
    }

    addressObject.value.streetNumber = props.addressProp?.streetNumber ?? civicNumber ?? null;
    addressObject.value.street = props.addressProp?.street ?? addressStreet;
    addressObject.value.city = props.addressProp?.city ?? app.value['city'] ?? null;
    addressObject.value.province = props.addressProp?.province ?? app.value['province'] ?? app.value['state'] ?? null;
    addressObject.value.postalCode = props.addressProp?.postalCode ?? app.value['postal_code'] ?? null;
}
</script>
